// Hand-rolled fuzzy matcher, dependency-free by design. Used by the importer to
// resolve free-text labels against the entity catalog.
//
// SELF-CONTAINED port: imports the catalog + its types from ./entityCatalog.

import { ENTITY_ITEMS, type EntityItem, type EntityItemId } from "./entityCatalog";

// Re-export so consumers (e.g. resolve.ts) can take the id type from this module
// alongside the match types. Under `isolatedModules` (which CRA enforces at build
// time) an imported type is not implicitly re-exported, so state it explicitly.
export type { EntityItemId } from "./entityCatalog";

// Inlined from the example app's modules/shared/types.ts.
export type EntityMatchConfidence = "exact" | "alias" | "fuzzy" | "manual";

export function normalizeLabel(raw: string): string {
  return raw
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const prev = new Array<number>(b.length + 1);
  const cur = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length];
}

export type MatchCandidate = { itemId: EntityItemId; score: number; confidence: EntityMatchConfidence };

type CatalogEntry = { item: EntityItem; normalizedLabel: string; normalizedAliases: string[] };

let catalogIndex: CatalogEntry[] | null = null;
function index(): CatalogEntry[] {
  if (!catalogIndex) {
    catalogIndex = ENTITY_ITEMS.map((item) => ({
      item,
      normalizedLabel: normalizeLabel(item.label),
      normalizedAliases: item.aliases.map(normalizeLabel),
    }));
  }
  return catalogIndex;
}

/** Rank every catalog item against a raw label. Returns candidates sorted
 *  best-first; empty when nothing clears the fuzzy threshold. */
export function matchLabel(rawLabel: string): MatchCandidate[] {
  const normalized = normalizeLabel(rawLabel);
  if (!normalized) return [];

  const exact: MatchCandidate[] = [];
  const alias: MatchCandidate[] = [];
  const fuzzy: MatchCandidate[] = [];

  for (const entry of index()) {
    if (normalized === entry.normalizedLabel || entry.normalizedAliases.includes(normalized)) {
      exact.push({ itemId: entry.item.id, score: 1, confidence: "exact" });
      continue;
    }
    const variants = [entry.normalizedLabel, ...entry.normalizedAliases];
    const substringHit = variants.some(
      (v) => v.length > 2 && (normalized.includes(v) || v.includes(normalized)),
    );
    if (substringHit) {
      alias.push({ itemId: entry.item.id, score: 0.85, confidence: "alias" });
      continue;
    }
    const bestDistance = Math.min(...variants.map((v) => levenshtein(normalized, v)));
    const threshold = Math.ceil(Math.max(normalized.length, ...variants.map((v) => v.length)) * 0.2);
    if (bestDistance <= threshold && bestDistance > 0) {
      const longest = Math.max(normalized.length, ...variants.map((v) => v.length));
      fuzzy.push({ itemId: entry.item.id, score: Math.max(0, 1 - bestDistance / longest), confidence: "fuzzy" });
    }
  }

  if (exact.length) return exact;
  if (alias.length) return alias.sort((a, b) => b.score - a.score);
  return fuzzy.sort((a, b) => b.score - a.score).slice(0, 3);
}
